<template>
  <form
    class="geopopup form -active"
    v-if="active"
    :action="'/' + selectedStoreViewKey"
  >
    <div class="container geopopup-wrapper">
      <div class="row align-items-start align-items-lg-center">
        <GlobeIcon class="d-none d-lg-block globe-icon" />
        <p
          class="geopopup-text col-10 col-lg mb-3 mb-lg-0"
          v-text="
            geoT(
              'Select a country or region to view content specific to your location.'
            )
          "
        ></p>
        <button
          type="button"
          class="close geopopup-close col-auto ml-auto d-lg-none"
          @click="closeMessage"
          :aria-label="geoT('Close Geolocation Popup')"
        >
          <i class="icon-close"></i>
        </button>
        <base-select
          class="-capitalize mb-0 ml-auto"
          :class="countries.length > 1 ? 'col-6 col-lg-2' : 'col-12 col-lg-4'"
          :selected="region"
          :hide-label="true"
          :placeholder="geoT('Choose your region')"
          :options="regions"
          id="region"
          name="region"
          v-model="region"
        />
        <base-select
          class="-capitalize mb-0 col-6 col-lg-2"
          :hide-label="true"
          :placeholder="geoT('Choose your country')"
          :selected="selectedStoreViewKey"
          :options="countries"
          v-show="countries.length > 1"
          id="country"
          name="country"
          v-model="selectedStoreViewKey"
        />
        <div class="col-12 col-lg-auto ml-auto mt-4 pt-3 mt-lg-0 pt-lg-0">
          <button
            class="geopopup-submit btn -white -block"
            v-text="geoT('Go to the country')"
            type="submit"
            :disabled="!selectedStoreViewKey"
          ></button>
        </div>
      </div>
    </div>
    <button
      type="button"
      class="close geopopup-close d-none d-lg-block"
      @click="closeMessage"
      :aria-label="geoT('Close Geolocation Popup')"
    >
      <i class="icon-close"></i>
    </button>
  </form>
</template>

<script>
import { ReadyHook } from 'vue-asyncready'
import { isServer } from '@vue-storefront/core/helpers'
import { getAkamaiPosition } from '../utilities'
import BaseSelect from '../components/form/BaseSelect'
import Vue from 'vue'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import config from 'config'
import GlobeIcon from './icons/GlobeIcon.vue'
import { currentBuildLocales } from '@vue-storefront/core/i18n/helpers'

const HIDE_FOR_TOT_DAYS = 7
const MS_PER_DAY = 1000 * 60 * 60 * 24

export default {
  name: 'GeoPopup',
  mixins: [ReadyHook],
  components: {
    GlobeIcon,
    BaseSelect
  },
  data() {
    return {
      isNewUser: true,
      active: false,
      cookieExpTime: HIDE_FOR_TOT_DAYS * MS_PER_DAY,
      akamaiCountry: null,
      currentCountry: null,
      currentLanguage: null,
      regions: [],
      supportedStores: [],
      region: null,
      selectedStoreViewKey: null,
      geoMessages: null
    }
  },
  computed: {
    countries() {
      return this.supportedStores
        .filter(store => store.region === this.region)
        .map(store => ({
          value: store.key,
          label: store.countryName
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }
  },
  watch: {
    region() {
      if (
        this.countries.find(
          country => country.value === this.selectedStoreViewKey
        )
      ) {
        return
      }

      const [firstCountry] = this.countries
      this.selectedStoreViewKey = firstCountry ? firstCountry.value : null
    }
  },
  methods: {
    // popup-only translation: falls back to the current store locale when the browser locale isn't bundled
    geoT(key) {
      return (this.geoMessages && this.geoMessages[key]) || this.$t(key)
    },
    async loadGeoMessages(locale) {
      try {
        const { default: messages } = await import(
          /* webpackChunkName: "lang-[request]" */ `@vue-storefront/core/i18n/resource/i18n/${locale}.json`
        )
        this.geoMessages = messages
      } catch (e) {
        // locale bundle missing: fall back to default.json instead of the current store locale
        try {
          const { default: defaultMessages } = await import(
            /* webpackChunkName: "lang-default" */ `@vue-storefront/core/i18n/resource/i18n/default.json`
          )
          this.geoMessages = defaultMessages
        } catch (fallbackError) {
          this.geoMessages = null
        }
      }
    },
    closeMessage() {
      this.active = false
      // used set timeout to wait the animation end and inner div removal
      // it's to handle an edge case: closing message with open menu.
      const expTime = new Date(Date.now() + this.cookieExpTime)
      Vue.prototype.$cookies.set('geoclosed', Date.now(), expTime)
      setTimeout(() => this.$emit('geoClosed'), 500)
    },
    populateRegionsAndCountries() {
      const { supportedStores, regions } = Object.entries(
        config.storeViews
      ).reduce(
        (acc, [key, country]) => {
          if (!country.include_in_menu || !country.i18n) {
            return acc
          }

          const store = {
            key,
            region: country.i18n.region,
            defaultLanguage: country.i18n.defaultLanguage,
            defaultCountry: country.i18n.defaultCountry,
            countryName: this.geoT(country.name)
          }

          acc.supportedStores.push(store)

          if (!acc.regions.find(region => region.value === store.region)) {
            acc.regions.push({
              value: store.region,
              label: this.geoT(store.region)
            })
          }

          return acc
        },
        {
          supportedStores: [],
          regions: []
        }
      )

      this.supportedStores = supportedStores
      this.regions = regions
    },
    availableCountries(country) {
      return this.supportedStores.filter(
        store => store.defaultCountry === country
      )
    },
    async initGeoPopup() {
      if (isServer) return
      if (Vue.prototype.$cookies.get('geoclosed')) return
      const preferredLanguages = navigator.languages
      const browserLocale = preferredLanguages?.find(l =>
        currentBuildLocales().includes(l)
      )
      if (browserLocale) {
        await this.loadGeoMessages(browserLocale)
      }
      this.populateRegionsAndCountries()
      let position

      try {
        position = await getAkamaiPosition()
      } catch (e) {
        return
      }

      if (!position) return
      this.akamaiCountry = position.country
      const availableAkamaiCountries = this.availableCountries(
        this.akamaiCountry
      )
      if (!availableAkamaiCountries.length) return
      const {
        i18n: { defaultCountry, defaultLanguage }
      } = currentStoreView()
      this.currentCountry = defaultCountry
      this.currentLanguage = defaultLanguage
      this.active = this.akamaiCountry !== this.currentCountry
      if (!this.active) return
      let matchedCountry = availableAkamaiCountries[0]
      if (typeof navigator !== 'undefined' && navigator.languages) {
        // look if any of the preferred languages is also in available countries and return the country
        const languageMatchedCountry = availableAkamaiCountries.find(store =>
          preferredLanguages.includes(store.defaultLanguage)
        )

        if (languageMatchedCountry) {
          matchedCountry = languageMatchedCountry
        }
      }
      if (matchedCountry) {
        this.region = matchedCountry.region
        this.selectedStoreViewKey = matchedCountry.key
      }

      if (this.active) {
        setTimeout(() => this.$emit('geoOpened'), 500)
      }
    }
  },
  mounted() {
    this.initGeoPopup()
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

.geopopup {
  --_zenith-header-text-link: var(--zenith-white);
  --zenith-form-input-background: transparent;
  --zenith-input-color: var(--zenith-white);

  position: relative;
  z-index: 100;
  background: var(--zenith-blue-darker);
  color: var(--zenith-white);
  overflow: hidden;
  max-height: 0;
  transition: all 0.4s ease-in-out;

  /deep/ .globe-icon {
    width: 3.2rem;
    height: 3.2rem;
  }

  &.-active {
    max-height: 400rem; // arbitrary large value to allow for content expansion and preserve logic used in TopMessage.vue
    padding-block: 2rem 3rem;

    @include bp(lg) {
      padding-block: 2.5rem;

      .container {
        padding-inline-end: 5rem;
      }
    }
  }

  &-text {
    font-size: 1.4rem;

    @include bp(lg) {
      font-size: 1.6rem;
    }
  }

  .form select + .input {
    padding-block: 1.3rem;
    height: 4.4rem;
  }

  button.close {
    background: none;
    border: none;
    color: var(--zenith-white);

    @include bp(lg) {
      position: absolute;
      right: 2rem;
      top: 50%;
      transform: translateY(-50%);
      padding: 0;
    }

    @include bp(xl) {
      right: 4rem;
    }

    i {
      display: block;
      font-size: em(20, 12);
      transition: all 0.2s ease-in-out;
    }

    &:hover,
    &:focus {
      i {
        transform: scale(1.2);
      }
    }
  }
}
</style>
